import { CARDINALITY } from '../../../../modernization-common/relationships/constants';
import type { CardinalityT, RelationshipT } from '../../../../modernization-common/relationships/types/relationship';
import { validateFrameRange } from '../../../../modernization-common/relationships/utils/relationship-editor-utils';

// ResolvedDraftT is the post-resolution shape that outputRelationshipsAtom works from.
// Frame indices are 0-indexed (storage format), matching what resolveFrameRanges produces.
// frameLimits are also 0-indexed — one window per source×target overlap.
export type ResolvedDraftT = {
  draftId: string;
  targetId: string;
  isInterval: boolean;
  frameRanges: Array<{
    startFrameIndex: number;
    endFrameIndex: number;
  }>;
  frameLimits: Array<{
    startFrameIndex: number;
    endFrameIndex: number;
  }>;
  targetAnnotationFrameRanges: Array<{
    startFrameIndex: number;
    endFrameIndex: number;
  }>;
};
export enum CardinalityErrorContext {
  SOURCE = 'source',
  TARGET = 'target',
  SOURCE_TARGET = 'source:target',
}
export type CardinalityErrorT = {
  context: CardinalityErrorContext;
  cardinalityValue: CardinalityT;
  conflictFrameRanges: Array<{
    startFrameIndex: number;
    endFrameIndex: number;
  }>;
};
export type RelationshipValidationErrors = {
  [draftId: string]: {
    frameRange: {
      startError: string | undefined;
      endError: string | undefined;
    };
    cardinality?: CardinalityErrorT;
  };
};
type CardinalityContextT = {
  cardinality: CardinalityT | undefined;
  sourceId: string;
  existingRelationships: RelationshipT[];
};
const frameRangesOverlap = (a: Array<{
  startFrameIndex: number;
  endFrameIndex: number;
}>, b: Array<{
  startFrameIndex: number;
  endFrameIndex: number;
}>): boolean => {
  if (a.length === 0 && b.length === 0) return true; // empty frame ranges means overlap since no frames are there for it.
  if (a.length === 0 || b.length === 0) return false; // one of the frame ranges is empty, so no overlap.
  return a.some(ra => b.some(rb => ra.startFrameIndex <= rb.endFrameIndex && rb.startFrameIndex <= ra.endFrameIndex));
};

// Returns an error if the draft is an exact duplicate of an existing store relationship or
// an earlier draft (same source + same target, overlapping frames).
const checkDuplicate = (draft: ResolvedDraftT, draftIndex: number, resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[], cardinalityValue: CardinalityT): CardinalityErrorT | undefined => {
  const storeMatch = existingRelationships.find(r => r.sourceAnnotationId === sourceId && r.targetAnnotationId === draft.targetId && frameRangesOverlap(draft.frameRanges, r.frameRanges));
  if (storeMatch) {
    return {
      context: CardinalityErrorContext.SOURCE_TARGET,
      cardinalityValue,
      conflictFrameRanges: storeMatch.frameRanges
    };
  }
  const draftMatch = resolvedDrafts.slice(0, draftIndex).find(earlier => earlier.targetId === draft.targetId && frameRangesOverlap(draft.frameRanges, earlier.frameRanges));
  if (draftMatch) {
    return {
      context: CardinalityErrorContext.SOURCE_TARGET,
      cardinalityValue,
      conflictFrameRanges: draftMatch.frameRanges
    };
  }
  return undefined;
};

// Returns an error if the same source already has a different target in overlapping frames
// (from store or an earlier draft). Used for ONE_TO_ONE and MANY_TO_ONE validation.
const checkSourceForward = (draft: ResolvedDraftT, draftIndex: number, resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[], cardinalityValue: CardinalityT): CardinalityErrorT | undefined => {
  // Check earlier drafts first — later draft gets the error, not the first one.
  const earlierDraft = resolvedDrafts.slice(0, draftIndex).find(earlier => earlier.targetId && frameRangesOverlap(draft.frameRanges, earlier.frameRanges));
  if (earlierDraft) {
    return {
      context: earlierDraft.targetId === draft.targetId ? CardinalityErrorContext.SOURCE_TARGET : CardinalityErrorContext.SOURCE,
      cardinalityValue,
      conflictFrameRanges: earlierDraft.frameRanges
    };
  }
  const storeMatch = existingRelationships.find(r => r.sourceAnnotationId === sourceId && r.targetAnnotationId !== draft.targetId && frameRangesOverlap(draft.frameRanges, r.frameRanges));
  if (storeMatch) {
    return {
      context: CardinalityErrorContext.SOURCE,
      cardinalityValue,
      conflictFrameRanges: storeMatch.frameRanges
    };
  }
  return undefined;
};

// Returns an error if the same target already has a different source in overlapping frames (store only —
// all drafts share the same sourceId so cross-draft target conflicts are impossible).
// Used for ONE_TO_ONE and ONE_TO_MANY validation.
const checkTargetReverse = (draft: ResolvedDraftT, sourceId: string, existingRelationships: RelationshipT[], cardinalityValue: CardinalityT): CardinalityErrorT | undefined => {
  const storeMatch = existingRelationships.find(r => r.targetAnnotationId === draft.targetId && r.sourceAnnotationId !== sourceId && frameRangesOverlap(draft.frameRanges, r.frameRanges));
  if (!storeMatch) return undefined;
  return {
    context: CardinalityErrorContext.TARGET,
    cardinalityValue,
    conflictFrameRanges: storeMatch.frameRanges
  };
};
const validateOneToOneCardinality = (resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[]): Record<string, CardinalityErrorT> => {
  const errors: Record<string, CardinalityErrorT> = {};
  for (let i = 0; i < resolvedDrafts.length; i++) {
    const draft = resolvedDrafts[i]!;
    if (!draft.targetId) continue;
    const error = checkDuplicate(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.ONE_TO_ONE) ?? checkTargetReverse(draft, sourceId, existingRelationships, CARDINALITY.ONE_TO_ONE) ?? checkSourceForward(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.ONE_TO_ONE);
    if (error) errors[draft.draftId] = error;
  }
  return errors;
};

// ONE_TO_MANY: a source can have many targets, but each target can only have one source.
const validateOneToManyCardinality = (resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[]): Record<string, CardinalityErrorT> => {
  const errors: Record<string, CardinalityErrorT> = {};
  for (let i = 0; i < resolvedDrafts.length; i++) {
    const draft = resolvedDrafts[i]!;
    if (!draft.targetId) continue;
    const error = checkDuplicate(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.ONE_TO_MANY) ?? checkTargetReverse(draft, sourceId, existingRelationships, CARDINALITY.ONE_TO_MANY);
    if (error) errors[draft.draftId] = error;
  }
  return errors;
};

// MANY_TO_ONE: many sources can share a target, but each source can only have one target.
const validateManyToOneCardinality = (resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[]): Record<string, CardinalityErrorT> => {
  const errors: Record<string, CardinalityErrorT> = {};
  for (let i = 0; i < resolvedDrafts.length; i++) {
    const draft = resolvedDrafts[i]!;
    if (!draft.targetId) continue;
    const error = checkDuplicate(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.MANY_TO_ONE) ?? checkSourceForward(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.MANY_TO_ONE);
    if (error) errors[draft.draftId] = error;
  }
  return errors;
};

// MANY_TO_MANY: no source or target constraints — only exact duplicates are invalid.
const validateManyToManyCardinality = (resolvedDrafts: ResolvedDraftT[], sourceId: string, existingRelationships: RelationshipT[]): Record<string, CardinalityErrorT> => {
  const errors: Record<string, CardinalityErrorT> = {};
  for (let i = 0; i < resolvedDrafts.length; i++) {
    const draft = resolvedDrafts[i]!;
    if (!draft.targetId) continue;
    const error = checkDuplicate(draft, i, resolvedDrafts, sourceId, existingRelationships, CARDINALITY.MANY_TO_MANY);
    if (error) errors[draft.draftId] = error;
  }
  return errors;
};

// Validates the resolved (0-indexed) frame ranges and optional cardinality constraints.
// Only emits entries for drafts that have at least one error — clean drafts are excluded.
export const validateResolvedDrafts = (resolvedDrafts: ResolvedDraftT[], cardinalityContext?: CardinalityContextT): RelationshipValidationErrors => {
  const cardinalityErrors = (() => {
    const {
      cardinality,
      sourceId,
      existingRelationships
    } = cardinalityContext ?? {};
    if (!cardinality || !sourceId || !existingRelationships) return {};
    if (cardinality === CARDINALITY.ONE_TO_ONE) return validateOneToOneCardinality(resolvedDrafts, sourceId, existingRelationships);
    if (cardinality === CARDINALITY.ONE_TO_MANY) return validateOneToManyCardinality(resolvedDrafts, sourceId, existingRelationships);
    if (cardinality === CARDINALITY.MANY_TO_ONE) return validateManyToOneCardinality(resolvedDrafts, sourceId, existingRelationships);
    if (cardinality === CARDINALITY.MANY_TO_MANY) return validateManyToManyCardinality(resolvedDrafts, sourceId, existingRelationships);
    return {};
  })();
  const result: RelationshipValidationErrors = {};
  resolvedDrafts.forEach(draft => {
    if (!draft.targetId) return;
    const range = draft.frameRanges[0];
    const frameRange = validateFrameRange(range?.startFrameIndex, range?.endFrameIndex, draft.isInterval, draft.frameLimits, draft.targetAnnotationFrameRanges);
    const cardinality = cardinalityErrors[draft.draftId];
    if (frameRange.startError || frameRange.endError || cardinality) {
      result[draft.draftId] = {
        frameRange,
        cardinality
      };
    }
  });
  return result;
};
export const hasDraftValidationErrors = (errors: RelationshipValidationErrors): boolean => Object.values(errors).length > 0;